import { LocalDate } from 'src/core/common/domain/date/local-date';
import { Task, TaskPrimitives } from 'src/core/master-data/domain/task';
import { UuidGenerator } from 'src/core/common/infrastructure/uuid/uuid-generator';
import { TaskQueueElement } from './task-queue-element';
import { QueueElementError, QueueElementType } from 'src/core/queue/domain/queue-element';
import { Nullable } from 'src/core/common/domain/types/types';

export type CreateTaskOperationStatus = {
  isCreatedToServer: boolean;
  createdTaskId: Nullable<string>;
};

type CreateTaskQueueElementPrimitives = {
  id: string;
  expected_date: string;
  created_at: string;
  type: string;
  task: TaskPrimitives;
  // createdTaskId is missing in elements stored before it was introduced
  operation_status: { isCreatedToServer: boolean; createdTaskId?: Nullable<string> };
  errors: Array<QueueElementError>;
};

export class CreateTaskQueueElement extends TaskQueueElement {
  constructor(
    id: string,
    public expectedDate: LocalDate,
    createdAt: LocalDate,
    public task: Task,
    public operationStatus: CreateTaskOperationStatus,
    public errors: Array<QueueElementError>
  ) {
    super(id, createdAt, QueueElementType.CREATE);
    this.label = this.task.subject;
    this.publicId = this.task.id;
  }

  static fromPayload(payload: CreateTaskQueueElementPrimitives) {
    return new this(
      payload.id,
      LocalDate.fromIsoString(payload.expected_date),
      LocalDate.fromIsoString(payload.created_at),
      Task.fromPrimitives(payload.task),
      {
        isCreatedToServer: payload.operation_status.isCreatedToServer,
        createdTaskId: payload.operation_status.createdTaskId ?? null,
      },
      payload.errors
    );
  }

  static fromTask(task: Task, operationStatus: CreateTaskOperationStatus, errors: Array<QueueElementError>) {
    return new this(UuidGenerator.random(), task.date.expected, LocalDate.now(), task, operationStatus, errors);
  }

  toPrimitives(): CreateTaskQueueElementPrimitives {
    return {
      id: this.id,
      type: this.type,
      created_at: this.createdAt.atom,
      expected_date: this.expectedDate.atom,
      task: this.task.toPrimitives(),
      operation_status: this.operationStatus,
      errors: this.errors,
    };
  }

  update(task: Task, operationStatus: CreateTaskOperationStatus, errors: Array<QueueElementError>) {
    this.task = task;
    this.operationStatus = operationStatus;
    this.errors = errors;
  }
}
